import React, { useEffect, useState } from 'react'
import { Button, Card, Col, Form, Input, InputNumber, List, Select, Space, Typography } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus, faTrash } from '@fortawesome/free-solid-svg-icons'
import CustomRow from '../../../reusable-components/CustomRow'
import { useWellnessGoals } from '../hooks/useWellnessGoals'

const { Title, Text } = Typography
const { Option } = Select

const WellnessGoals = ({ wellnessGoals, setWellnessGoals }) => {
  const [form] = Form.useForm()
  const [selectedGoal, setSelectedGoal] = useState(null)
  const { wellnessGoalsOptions, isLoading } = useWellnessGoals()

  useEffect(() => {
    form.resetFields()
  }, [selectedGoal, form])

  const handleAddGoal = (values) => {
    const newGoal = {
      id: Date.now(),
      name: values.name,
      category: values.category,
      description: values.description,
      targetValue: values.targetValue,
      targetUnit: values.targetUnit,
      timeframe: values.timeframe,
      milestones: values.milestones || [],
      trackingMethod: values.trackingMethod
    }

    setWellnessGoals([...wellnessGoals, newGoal])
    form.resetFields()
  }

  const handleDeleteGoal = (id) => {
    setWellnessGoals(wellnessGoals.filter((item) => item.id !== id))
    if (selectedGoal && selectedGoal.id === id) {
      setSelectedGoal(null)
    }
  }

  const handleEditGoal = () => {
    if (!selectedGoal) return

    const values = form.getFieldsValue()
    const updatedGoals = wellnessGoals.map((item) => {
      if (item.id === selectedGoal.id) {
        return {
          ...item,
          name: values.name,
          category: values.category,
          description: values.description,
          targetValue: values.targetValue,
          targetUnit: values.targetUnit,
          timeframe: values.timeframe,
          milestones: values.milestones || [],
          trackingMethod: values.trackingMethod
        }
      }
      return item
    })

    setWellnessGoals(updatedGoals)
    setSelectedGoal(null)
  }

  return (
    <CustomRow>
      <Col span={24}>
        <Title level={4}>Personal Wellness Goals</Title>
        <Text>Configure personal wellness objectives, targets, and progress tracking for the wellness plan.</Text>
      </Col>

      <Col span={12} style={{ paddingRight: 12 }}>
        <Card title='Wellness Goals' loading={isLoading}>
          <List
            dataSource={wellnessGoals}
            renderItem={(item) => (
              <List.Item
                key={item.id}
                actions={[
                  <Button
                    type='text'
                    danger
                    onClick={() => handleDeleteGoal(item.id)}
                    icon={<FontAwesomeIcon icon={faTrash} />}
                  />
                ]}
              >
                <List.Item.Meta
                  title={item.name}
                  description={`${item.category} - Target: ${item.targetValue} ${item.targetUnit}`}
                  style={{ cursor: 'pointer' }}
                  onClick={() => setSelectedGoal(item)}
                />
              </List.Item>
            )}
            footer={
              <Button
                type='dashed'
                block
                onClick={() => setSelectedGoal(null)}
                icon={<FontAwesomeIcon icon={faPlus} />}
              >
                Add New Wellness Goal
              </Button>
            }
          />
        </Card>
      </Col>

      <Col span={12}>
        <Card title={selectedGoal ? 'Edit Wellness Goal' : 'Add Wellness Goal'}>
          <Form
            form={form}
            layout='vertical'
            onFinish={selectedGoal ? handleEditGoal : handleAddGoal}
            initialValues={selectedGoal || {}}
          >
            <Form.Item name='name' label='Goal Name' rules={[{ required: true, message: 'Please enter a name' }]}>
              <Input placeholder='e.g., Increase Daily Steps' />
            </Form.Item>

            <Form.Item
              name='category'
              label='Goal Category'
              rules={[{ required: true, message: 'Please select a category' }]}
            >
              <Select placeholder='Select goal category'>
                {wellnessGoalsOptions.map((option) => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item name='description' label='Description'>
              <Input.TextArea rows={2} placeholder='Describe the wellness goal...' />
            </Form.Item>

            <Form.Item
              name='targetValue'
              label='Target Value'
              rules={[{ required: true, message: 'Please specify target value' }]}
            >
              <InputNumber min={0} />
            </Form.Item>

            <Form.Item
              name='targetUnit'
              label='Target Unit'
              rules={[{ required: true, message: 'Please specify unit' }]}
            >
              <Select placeholder='Select unit'>
                <Option value='steps'>Steps</Option>
                <Option value='minutes'>Minutes</Option>
                <Option value='hours'>Hours</Option>
                <Option value='days'>Days</Option>
                <Option value='sessions'>Sessions</Option>
                <Option value='servings'>Servings</Option>
                <Option value='glasses'>Glasses</Option>
                <Option value='percentage'>Percentage</Option>
                <Option value='score'>Score</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name='timeframe'
              label='Goal Timeframe'
              rules={[{ required: true, message: 'Please select timeframe' }]}
            >
              <Select placeholder='Select timeframe'>
                <Option value='daily'>Daily</Option>
                <Option value='weekly'>Weekly</Option>
                <Option value='monthly'>Monthly</Option>
                <Option value='quarterly'>Quarterly</Option>
                <Option value='annually'>Annually</Option>
                <Option value='custom'>Custom</Option>
              </Select>
            </Form.Item>

            <Form.Item name='milestones' label='Milestones'>
              <Select mode='multiple' placeholder='Select milestones'>
                <Option value='25%'>25% of target</Option>
                <Option value='50%'>50% of target</Option>
                <Option value='75%'>75% of target</Option>
                <Option value='100%'>100% of target (complete)</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name='trackingMethod'
              label='Tracking Method'
              rules={[{ required: true, message: 'Please select tracking method' }]}
            >
              <Select placeholder='Select tracking method'>
                <Option value='manual-entry'>Manual Entry</Option>
                <Option value='wearable-device'>Wearable Device</Option>
                <Option value='mobile-app'>Mobile App</Option>
                <Option value='calendar-tracking'>Calendar Tracking</Option>
                <Option value='healthcare-provider'>Healthcare Provider</Option>
                <Option value='self-assessment'>Self-Assessment</Option>
              </Select>
            </Form.Item>

            <Form.Item>
              <Space>
                <Button type='primary' htmlType='submit'>
                  {selectedGoal ? 'Update' : 'Add'} Wellness Goal
                </Button>
                {selectedGoal && <Button onClick={() => setSelectedGoal(null)}>Cancel</Button>}
              </Space>
            </Form.Item>
          </Form>
        </Card>
      </Col>
    </CustomRow>
  )
}

export default WellnessGoals
